const API_BASE = '/api/v1/screening';

export interface Screening {
  id: number;
  userId: string;
  name: string;
  screeningType: string;
  recommendedDate: string;
  lastPerformedDate: string | null;
  frequency: string | null;
  reminderDays: number;
  priority: string;
  notes: string | null;
  reminded: boolean;
  remindedAt: string | null;
  emailReminderEnabled: boolean;
  completed: boolean;
  completedAt: string | null;
  createdAt: string;
}

export interface ScreeningStats {
  scheduled: number;
  nextThirtyDays: number;
  overdue: number;
  completed: number;
}

export interface ScreeningCreateData {
  name: string;
  screeningType: string;
  recommendedDate: string;
  lastPerformedDate?: string;
  frequency?: string;
  reminderDays?: number;
  priority?: string;
  notes?: string;
  emailReminderEnabled?: boolean;
}

export interface ScreeningUpdateData extends Partial<ScreeningCreateData> {
  completed?: boolean;
}

// Get all screenings
export async function getScreenings(): Promise<{ success: boolean; screenings: Screening[] }> {
  const response = await fetch(API_BASE, {
    credentials: 'include',
  });
  if (!response.ok) throw new Error('Errore nel caricamento degli screening');
  return response.json();
}

// Get screening statistics
export async function getScreeningStats(): Promise<{ success: boolean; stats: ScreeningStats }> {
  const response = await fetch(`${API_BASE}/stats`, {
    credentials: 'include',
  });
  if (!response.ok) throw new Error('Errore nel caricamento delle statistiche');
  return response.json();
}

// Get a specific screening
export async function getScreening(id: number): Promise<{ success: boolean; screening: Screening }> {
  const response = await fetch(`${API_BASE}/${id}`, {
    credentials: 'include',
  });
  if (!response.ok) throw new Error('Screening non trovato');
  return response.json();
}

// Create a new screening
export async function createScreening(data: ScreeningCreateData): Promise<{ success: boolean; screening: Screening }> {
  const response = await fetch(API_BASE, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
  if (!response.ok) throw new Error('Errore nella creazione dello screening');
  return response.json();
}

// Update a screening
export async function updateScreening(id: number, data: ScreeningUpdateData): Promise<{ success: boolean; screening: Screening }> {
  const response = await fetch(`${API_BASE}/${id}`, {
    method: 'PUT',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
  if (!response.ok) throw new Error('Errore nell\'aggiornamento dello screening');
  return response.json();
}

// Mark screening as completed
export async function completeScreening(id: number): Promise<{ success: boolean; screening: Screening }> {
  const response = await fetch(`${API_BASE}/${id}/complete`, {
    method: 'POST',
    credentials: 'include',
  });
  if (!response.ok) throw new Error('Errore nel completamento dello screening');
  return response.json();
}

// Reschedule a screening
export async function rescheduleScreening(id: number, nextDate?: string): Promise<{ success: boolean; screening: Screening }> {
  const response = await fetch(`${API_BASE}/${id}/reschedule`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ nextDate }),
  });
  if (!response.ok) throw new Error('Errore nella riprogrammazione dello screening');
  return response.json();
}

// Delete a screening
export async function deleteScreening(id: number): Promise<{ success: boolean }> {
  const response = await fetch(`${API_BASE}/${id}`, {
    method: 'DELETE',
    credentials: 'include',
  });
  if (!response.ok) throw new Error('Errore nell\'eliminazione dello screening');
  return response.json();
}

// Utility: Format date to Italian format
export function formatDateItalian(dateString: string | null): string {
  if (!dateString) return '-';
  return new Date(dateString).toLocaleDateString('it-IT', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
}

// Utility: Get status badge info based on date and completion
export function getScreeningStatus(screening: Screening): { label: string; color: string; bgColor: string } {
  if (screening.completed) {
    return { label: 'Completato', color: 'text-green-700', bgColor: 'bg-green-100' };
  }

  const now = new Date();
  const recommendedDate = new Date(screening.recommendedDate);
  const daysUntil = Math.ceil((recommendedDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));

  if (daysUntil < 0) {
    return { label: 'Scaduto', color: 'text-red-700', bgColor: 'bg-red-100' };
  } else if (daysUntil <= 30) {
    return { label: 'Prossimo', color: 'text-amber-700', bgColor: 'bg-amber-100' };
  } else {
    return { label: 'Programmato', color: 'text-blue-700', bgColor: 'bg-blue-100' };
  }
}

// Utility: Get priority info
export function getPriorityInfo(priority: string): { label: string; color: string; bgColor: string } {
  switch (priority) {
    case 'urgente':
      return { label: 'Urgente', color: 'text-red-700', bgColor: 'bg-red-100' };
    case 'alta':
      return { label: 'Alta', color: 'text-orange-700', bgColor: 'bg-orange-100' };
    default:
      return { label: 'Normale', color: 'text-gray-700', bgColor: 'bg-gray-100' };
  }
}

// Utility: Get category/type info
export function getCategoryInfo(screeningType: string): { label: string; color: string; bgColor: string } {
  switch (screeningType.toLowerCase()) {
    case 'oncologico':
      return { label: 'Oncologico', color: 'text-purple-700', bgColor: 'bg-purple-100' };
    case 'cardiovascolare':
      return { label: 'Cardiovascolare', color: 'text-red-700', bgColor: 'bg-red-100' };
    case 'osseo':
      return { label: 'Osseo', color: 'text-amber-700', bgColor: 'bg-amber-100' };
    case 'metabolico':
      return { label: 'Metabolico', color: 'text-blue-700', bgColor: 'bg-blue-100' };
    case 'oculistico':
      return { label: 'Oculistico', color: 'text-cyan-700', bgColor: 'bg-cyan-100' };
    case 'dentistico':
      return { label: 'Dentistico', color: 'text-teal-700', bgColor: 'bg-teal-100' };
    default:
      return { label: 'Generale', color: 'text-gray-700', bgColor: 'bg-gray-100' };
  }
}

// Predefined screening types (categories)
export const screeningCategories = [
  { value: 'generale', label: 'Generale' },
  { value: 'oncologico', label: 'Oncologico' },
  { value: 'cardiovascolare', label: 'Cardiovascolare' },
  { value: 'osseo', label: 'Osseo' },
  { value: 'metabolico', label: 'Metabolico' },
  { value: 'oculistico', label: 'Oculistico' },
  { value: 'dentistico', label: 'Dentistico' },
];

// Predefined screening types with name and category
export const screeningTypes = [
  { value: 'colonscopia', name: 'Colonscopia', category: 'oncologico', label: 'Colonscopia (Oncologico)' },
  { value: 'mammografia', name: 'Mammografia', category: 'oncologico', label: 'Mammografia (Oncologico)' },
  { value: 'pap_test', name: 'PAP Test', category: 'oncologico', label: 'PAP Test (Oncologico)' },
  { value: 'psa', name: 'PSA', category: 'oncologico', label: 'PSA (Oncologico)' },
  { value: 'tc_torace', name: 'TC Torace', category: 'oncologico', label: 'TC Torace (Oncologico)' },
  { value: 'densitometria', name: 'Densitometria Ossea', category: 'osseo', label: 'Densitometria Ossea (Osseo)' },
  { value: 'ecg', name: 'ECG', category: 'cardiovascolare', label: 'ECG (Cardiovascolare)' },
  { value: 'ecocardiogramma', name: 'Ecocardiogramma', category: 'cardiovascolare', label: 'Ecocardiogramma (Cardiovascolare)' },
  { value: 'visita_oculistica', name: 'Visita Oculistica', category: 'generale', label: 'Visita Oculistica (Generale)' },
  { value: 'visita_dermatologica', name: 'Visita Dermatologica', category: 'generale', label: 'Visita Dermatologica (Generale)' },
  { value: 'analisi_sangue', name: 'Analisi del Sangue', category: 'generale', label: 'Analisi del Sangue (Generale)' },
  { value: 'altro', name: '', category: 'generale', label: 'Altro' },
];

// Predefined frequencies
export const frequencyOptions = [
  { value: '3 mesi', label: 'Ogni 3 mesi' },
  { value: '6 mesi', label: 'Ogni 6 mesi' },
  { value: '12 mesi', label: 'Ogni anno' },
  { value: '24 mesi', label: 'Ogni 2 anni' },
  { value: '36 mesi', label: 'Ogni 3 anni' },
  { value: '48 mesi', label: 'Ogni 4 anni' },
  { value: '60 mesi', label: 'Ogni 5 anni' },
];

// Predefined reminder options
export const reminderOptions = [
  { value: 7, label: '1 settimana prima' },
  { value: 14, label: '2 settimane prima' },
  { value: 30, label: '1 mese prima' },
  { value: 60, label: '2 mesi prima' },
  { value: 90, label: '3 mesi prima' },
];

// Common screening suggestions
export const commonScreenings = [
  { name: 'Analisi del Sangue', type: 'generale', frequency: '6 mesi' },
  { name: 'Colonscopia', type: 'oncologico', frequency: '48 mesi' },
  { name: 'Mammografia', type: 'oncologico', frequency: '24 mesi' },
  { name: 'PSA', type: 'oncologico', frequency: '12 mesi' },
  { name: 'Pap Test', type: 'oncologico', frequency: '36 mesi' },
  { name: 'Densitometria Ossea', type: 'generale', frequency: '24 mesi' },
  { name: 'Visita Cardiologica', type: 'cardiovascolare', frequency: '12 mesi' },
  { name: 'ECG', type: 'cardiovascolare', frequency: '12 mesi' },
  { name: 'Ecocardiogramma', type: 'cardiovascolare', frequency: '24 mesi' },
  { name: 'Visita Oculistica', type: 'oculistico', frequency: '24 mesi' },
  { name: 'Visita Dentistica', type: 'dentistico', frequency: '6 mesi' },
  { name: 'Glicemia', type: 'metabolico', frequency: '12 mesi' },
  { name: 'Profilo Lipidico', type: 'metabolico', frequency: '12 mesi' },
  { name: 'Tiroide (TSH)', type: 'metabolico', frequency: '12 mesi' },
  { name: 'Ecografia Addominale', type: 'generale', frequency: '24 mesi' },
  { name: 'Spirometria', type: 'generale', frequency: '24 mesi' },
];
